import type {
    CURRENCY, CURRENCY_SYMBOL, DISCOUNT_SUBTYPE, DISCOUNT_TYPE, ORDER_STATUS,
    ORDER_TYPE, STORE_PLATFORM
} from '../constants';

import type {
    Cents, Email, HTTPUrl, JSONObject, PartialAddress,
    PartialStreetAddress, Percentage, PhoneNumber
} from './basic';
import type { Milliseconds } from './time';

import { assertTypeType } from './util';

export type PercentageDiscount = {
    type : DISCOUNT_TYPE.PERCENTAGE,
    subType ?: DISCOUNT_SUBTYPE,
    amount : Percentage,
    amountLabel ?: string,
    currency ?: CURRENCY,
    actualAmount ?: Cents,
    code ?: string,
    variantID ?: string,
    name ?: string,
    expiry ?: Milliseconds,
};

export type AmountDiscount = {
    type : DISCOUNT_TYPE.AMOUNT,
    subType ?: DISCOUNT_SUBTYPE,
    amount : Cents,
    amountLabel ?: string,
    currency ?: CURRENCY,
    code ?: string,
    variantID ?: string,
    loyaltyPoints ?: number,
    name ?: string,
    expiry ?: Milliseconds,
};

export type CodeDiscount = {
    type : DISCOUNT_TYPE.CODE,
    subType ?: DISCOUNT_SUBTYPE.PRICE_REDUCTION,
    code : string,
    actualAmount ?: Cents,
    amountLabel ?: string,
    name ?: string,
    expiry ?: Milliseconds,
};

export type Discount =
    | PercentageDiscount
    | AmountDiscount
    | CodeDiscount;

export type ShippingOption = {
    id : string | undefined,
    label : string,
    amount : Cents,
};

export type VariantCartInputItem = {
    name ?: string,
    quantity : number,
    variantID : string,
    productID ?: string,
    properties ?: JSONObject,
    discount ?: Discount,
};

export type ProductSearchVariant = {
    name ?: string,
    quantity : number,
    variantID : string,
    productID : string,
    description ?: string,
    variantImage ?: HTTPUrl,
    handle ?: string,
    price ?: number,
};

export type ProductCartInputItem = {
    name ?: string,
    quantity : number,
    productID : string,
    variantID ?: string,
    properties ?: JSONObject,
    discount ?: Discount,
};

export type CartInputItem =
    | VariantCartInputItem
    | ProductCartInputItem;

export type ShopifyCartInputItem =
    | VariantCartInputItem;

export type WooCartInputItem =
    | ProductCartInputItem;

export type ClientCartInput = {
    items : Array<VariantCartInputItem>,
};

export type ShippingOptionInput =
    | {
        id : string,
    }
    | {
        amount : Cents,
        label : string,
    };

export type OrderBuyerInput = {
    phone ?: PhoneNumber,
    email ?: Email,
    firstName ?: string,
    lastName ?: string,
    billingAddress ?: PartialAddress,
    shippingAddress ?: PartialAddress,
    partialStreetAddress ?: PartialStreetAddress,
};

export type ShopifyCartInput = {
    currency ?: CURRENCY,
    totals ?: {
        shippingAmount ?: Cents,
    },
    items : ReadonlyArray<ShopifyCartInputItem>,
    discount ?: Discount,
    intrinsicDiscount ?: Discount,
    itemPropertiesValid ?: boolean,
};

export type ShopifyOrderInput = {
    platform : STORE_PLATFORM.SHOPIFY,
    type : ORDER_TYPE,
    cart : ShopifyCartInput,
    buyer ?: OrderBuyerInput,
    shippingOption ?: ShippingOptionInput,
    shippingRequired : boolean,
};

export type WooCartInput = {
    currency ?: CURRENCY,
    totals ?: {
        shippingAmount ?: Cents,
    },
    items : ReadonlyArray<WooCartInputItem>,
    discount ?: Discount,
    intrinsicDiscount ?: Discount,
    itemPropertiesValid ?: boolean,
};

export type WooOrderInput = {
    platform : STORE_PLATFORM.WOO,
    externalID ?: string,
    type : ORDER_TYPE,
    cart : WooCartInput,
    buyer ?: OrderBuyerInput,
    shippingOption ?: ShippingOptionInput,
    shippingRequired : boolean,
};

export type OrderInput =
    | ShopifyOrderInput
    | WooOrderInput;

export type ClientOrderInput = {
    platform : STORE_PLATFORM,
    type : ORDER_TYPE,
    cart : ClientCartInput,
    buyer ?: OrderBuyerInput,
    shippingOption ?: ShippingOptionInput,
};

export type PartialShopifyOrderInput = {
    cart ?: Partial<ShopifyCartInput>,
    buyer ?: Partial<OrderBuyerInput>,
    shippingOption ?: ShippingOptionInput,
    shippingRequired ?: boolean,
    platform : STORE_PLATFORM.SHOPIFY,
};

export type PartialWooOrderInput = {
    cart ?: Partial<WooCartInput>,
    buyer ?: Partial<OrderBuyerInput>,
    shippingOption ?: ShippingOptionInput,
    shippingRequired ?: boolean,
    platform : STORE_PLATFORM.WOO,
};

export type PartialOrderInput =
    | PartialShopifyOrderInput
    | PartialWooOrderInput;

export type CartTotals = {
    totalAmount : Cents,
    originalTotalAmount : Cents,
    totalBeforeDiscountAmount : Cents,
    originalSubTotalAmount : Cents,
    subTotalAmount : Cents,
    subTotalBeforeDiscountAmount : Cents,
    intrinsicDiscountAmount : Cents,
    intrinsicDiscountAmountAsPercentage : Percentage,
    discountAmount : Cents,
    shippingAmount : Cents,
    taxAmount : Cents,
    handlingAmount : Cents,
    taxAndHandlingAmount : Cents,
    discountAmountAsPercentage : Percentage,
};

type VariantCartItem = {
    name : string,
    productID : string,
    variantID : string,
    quantity : number,
    amount : Cents,
    originalAmount : Cents,
    totalAmount : Cents,
    originalTotalAmount : Cents,
    url ?: string,
    image ?: HTTPUrl,
    taxable : boolean,
    taxLines ?: ReadonlyArray<{
        rate : number,
    }>,
    properties : JSONObject | undefined,
    discount ?: Discount,
};

type ProductCartItem = {
    name : string,
    quantity : number,
    productID : string,
    variantID ?: string,
    amount : Cents,
    originalAmount : Cents,
    totalAmount : Cents,
    originalTotalAmount : Cents,
    image ?: HTTPUrl,
    url ?: string,
    discount ?: undefined,
    properties ?: JSONObject | undefined,
};

export type ShopifyCartItem =
    | VariantCartItem;

export type WooCartItem =
    | ProductCartItem;

export type CartItem =
    | ShopifyCartItem
    | WooCartItem;

export type Cart<Item> = {
    currency : CURRENCY,
    currencySymbol : CURRENCY_SYMBOL,
    items : ReadonlyArray<Item>,
    discount ?: Discount,
    intrinsicDiscount ?: Discount,
    totals : CartTotals,
    image : string | undefined,
    itemPropertiesValid ?: boolean,
};

export type OrderBuyer = {
    phone ?: PhoneNumber,
    email ?: Email,
    billingAddress ?: PartialAddress,
    shippingAddress ?: PartialAddress,
};

export type OrderMetadata = {
    orderType : ORDER_TYPE,
    flatShippingAmount ?: Cents,
    shippingRequired : boolean,
    discount ?: Discount,
};

export type ShopifyOrder = {
    platform : STORE_PLATFORM.SHOPIFY,
    type : ORDER_TYPE,
    status : ORDER_STATUS,
    id : string,
    cart : Cart<ShopifyCartItem>,
    buyer : OrderBuyer,
    shippingOptions : ReadonlyArray<ShippingOption>,
    shippingOption ?: ShippingOption,
    shippingRequired : boolean,

    metadata : OrderMetadata,
    paymentURL : HTTPUrl,
    adminURL : HTTPUrl,

    orderNumber ?: string,
    finalOrderID ?: string,
    statusURL ?: HTTPUrl,
    fulfilledAt ?: Milliseconds,
};

export type WooOrder = {
    platform : STORE_PLATFORM.WOO,
    type : ORDER_TYPE,
    status : ORDER_STATUS,
    id : string,
    cart : Cart<WooCartItem>,
    buyer : OrderBuyer,
    shippingOptions : ReadonlyArray<ShippingOption>,
    shippingOption ?: ShippingOption,
    shippingRequired : boolean,

    metadata : OrderMetadata,
    paymentURL : HTTPUrl,
    adminURL ?: HTTPUrl,

    orderNumber ?: string,
    finalOrderID ?: string,
    statusURL ?: HTTPUrl,
    fulfilledAt ?: Milliseconds,
    createdAt ?: Milliseconds,
};

export type Order =
    | ShopifyOrder
    | WooOrder;

assertTypeType<OrderInput, Order>();

export type StoreLink = {
    offeredDiscount ?: Discount | undefined,
    shippingTotal ?: Cents,
    discountExpiry ?: Milliseconds,
};

export type Product = {
    id : string,
    name : string,
    variants : ReadonlyArray<{
        id : string,
        name : string,
        variantName : string,
        amount : Cents,
        originalAmount : Cents,
        options : ReadonlyArray<{
            type : string,
            name : string,
        }>,
        image ?: HTTPUrl,
    }>,
    image ?: HTTPUrl,
    options : ReadonlyArray<{
        type : string,
        values : ReadonlyArray<{
            name : string,
        }>,
    }>,
    tags : ReadonlyArray<string>,
    description ?: string,
    fromPrice : Cents,
    toPrice : Cents,
};

export type ProductCollection = {
    id : string,
    title : string,
};
